import { Fragment, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import type { AppSpendRow } from '@/services/adminApi';

interface Props {
  apps: AppSpendRow[];
}

/**
 * Pure-rendering table: per-app expandable groups, per-machine rows with
 * inline breakdown when expanded.
 *
 * Suspended apps collapse by default; everything else expands by default.
 * Unknown machine type ⇒ MTD/projection cells render "—" with a tooltip; the
 * parent app row also shows "+ unknown" hint so admins know the displayed
 * total is a lower bound.
 */
export function SpendingTable({ apps }: Props): JSX.Element {
  const { t } = useTranslation();
  if (apps.length === 0) {
    return <p className="muted">{t('admin.spending.noApps')}</p>;
  }
  return (
    <div className="spending-groups">
      {apps.map((app) => (
        <AppSection key={app.appName} app={app} />
      ))}
    </div>
  );
}

function AppSection({ app }: { app: AppSpendRow }): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState<boolean>(app.appStatus !== 'suspended');
  const machineCount = app.machines.length;
  return (
    <section className="admin-card" data-testid={`spend-app-${app.appName}`}>
      <button
        type="button"
        className="spending-app-header"
        onClick={(): void => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={`spend-app-body-${app.appName}`}
      >
        <span className="spending-app-name">
          <code>{app.appName}</code>
          <StatusBadge status={app.appStatus} />
          {app.region && <span className="muted spending-region">{app.region}</span>}
        </span>
        <span className="spending-app-totals">
          <span data-testid={`spend-app-mtd-${app.appName}`}>{t('admin.spending.mtdSuffix', { amount: formatUsd(app.mtdCostUsd) })}</span>
          <span className="muted" data-testid={`spend-app-proj-${app.appName}`}>
            {t('admin.spending.projSuffix', { amount: formatUsd(app.projectedMonthlyUsd) })}
          </span>
          {app.hasUnknownMachineTypes && (
            <span className="text-dim" title={t('admin.spending.unknownHintTitle')}>
              {t('admin.spending.unknownAdd')}
            </span>
          )}
          <span aria-hidden="true">{open ? '▾' : '▸'}</span>
        </span>
      </button>
      {open && (
        <div id={`spend-app-body-${app.appName}`}>
          {app.fetchError && (
            <p className="error" role="alert"><Trans i18nKey="admin.spending.listError" components={{ code: <code /> }} values={{ error: app.fetchError }} /></p>
          )}
          {!app.fetchError && machineCount === 0 && (
            <p className="muted">{t('admin.spending.noMachines')}</p>
          )}
          {!app.fetchError && machineCount > 0 && (
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th scope="col">{t('admin.spending.colMachine')}</th>
                    <th scope="col">{t('admin.spending.colRegion')}</th>
                    <th scope="col">{t('admin.spending.colState')}</th>
                    <th scope="col">{t('admin.spending.colConfig')}</th>
                    <th scope="col" className="num">{t('admin.spending.colMtd')}</th>
                    <th scope="col" className="num">{t('admin.spending.colProjected')}</th>
                    <th scope="col" aria-label={t('admin.common.details')} />
                  </tr>
                </thead>
                <tbody>
                  {app.machines.map((m) => <MachineRow key={m.machineId} appName={app.appName} m={m} />)}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function MachineRow({ appName, m }: { appName: string; m: AppSpendRow['machines'][number] }): JSX.Element {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const config = `${m.cpuKind} · ${m.cpus} CPU · ${formatMb(m.memoryMb)}`;
  const isUnknown = m.mtdCostUsd === null;
  return (
    <Fragment>
      <tr data-testid={`spend-machine-${appName}-${m.machineId}`}>
        <td><code>{m.machineId}</code></td>
        <td>{m.region}</td>
        <td><StateBadge state={m.state} /></td>
        <td>{config}</td>
        <td className="num">
          {isUnknown ? (
            <span className="text-dim" title={t('admin.spending.unknownTitle', { cpuKind: m.cpuKind })}>—</span>
          ) : formatUsd(m.mtdCostUsd ?? 0)}
        </td>
        <td className="num">
          {isUnknown ? (
            <span className="text-dim" title={t('admin.spending.unknownTitle', { cpuKind: m.cpuKind })}>—</span>
          ) : formatUsd(m.projectedMonthlyUsd ?? 0)}
        </td>
        <td className="actions">
          <button
            type="button"
            className="btn-ghost"
            onClick={(): void => setExpanded((v) => !v)}
            aria-expanded={expanded}
            aria-controls={`spend-machine-details-${m.machineId}`}
            aria-label={expanded ? t('admin.common.hideDetails', { name: m.machineId }) : t('admin.common.showDetails', { name: m.machineId })}
          >
            {expanded ? '▾' : '▸'}
          </button>
        </td>
      </tr>
      {expanded && (
        <tr id={`spend-machine-details-${m.machineId}`} className="admin-redemptions-row">
          <td colSpan={7}>
            <dl className="health-details">
              <dt>{t('admin.spending.created')}</dt>
              <dd>{new Date(m.createdAt).toLocaleString()}</dd>
              <dt>{t('admin.spending.uptimeMtd')}</dt>
              <dd>{t('admin.spending.uptimeHours', { hours: (m.uptimeSecMtd / 3600).toFixed(1) })}</dd>
              <dt>{t('admin.spending.cpu')}</dt>
              <dd>{formatUsd(m.breakdown.cpuUsd)}</dd>
              <dt>{t('admin.spending.memory')}</dt>
              <dd>{formatUsd(m.breakdown.memoryUsd)}</dd>
              <dt>{t('admin.spending.volumes')}</dt>
              <dd>{formatUsd(m.breakdown.volumesUsd)}</dd>
              <dt>{t('admin.spending.ipv4')}</dt>
              <dd>{formatUsd(m.breakdown.ipv4Usd)}</dd>
            </dl>
          </td>
        </tr>
      )}
    </Fragment>
  );
}

function StatusBadge({ status }: { status: AppSpendRow['appStatus'] }): JSX.Element {
  return <span className={`time-pill status-pill--${status}`}>{status}</span>;
}

function StateBadge({ state }: { state: string }): JSX.Element {
  const known = state === 'started' || state === 'stopped' || state === 'suspended' || state === 'destroyed';
  const cls = known ? `state-pill state-pill--${state}` : 'state-pill state-pill--unknown';
  return <span className={cls}>{state}</span>;
}

export function formatUsd(n: number): string {
  // 2 decimals up to $9.99, then 1 decimal above $10 for visual density.
  if (Math.abs(n) < 10) return `$${n.toFixed(2)}`;
  return `$${n.toFixed(1)}`;
}

export function formatMb(mb: number): string {
  if (mb >= 1024) return `${(mb / 1024).toFixed(mb % 1024 === 0 ? 0 : 1)} GB`;
  return `${mb} MB`;
}
